import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Calculator, Building2, Megaphone, Wrench, Palette, LogOut, ShieldCheck, PlayCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, PF_TOKEN_KEY } from "../lib/api";
import { PfLogin, PfPasswordCard, PfRecoveryEmailCard, PfLinkCard } from "../components/platform/PfAuth";
import { LogoMark } from "../components/Logo";
import { AdminOrgsDialog } from "../components/AdminOrgsDialog";
import { PcOverview } from "../components/platform/PcOverview";
import { PcAccounting } from "../components/platform/PcAccounting";
import { PcMarketing, PcTools } from "../components/platform/PcTools";
import { PcVideoUsage } from "../components/platform/PcVideoUsage";
import { PcRabet } from "../components/platform/PcRabet";
import { PcInsurance } from "../components/platform/PcInsurance";
import { PcTutorials } from "../components/platform/PcTutorials";
import { PcBackups } from "../components/platform/PcBackups";

const NAV = [
  ["overview", "لوحة التحكم", LayoutDashboard],
  ["accounting", "المحاسبة", Calculator],
  ["companies", "الشركات والاشتراكات", Building2],
  ["marketing", "التسويق", Megaphone],
  ["insurance", "سوق التأمين", ShieldCheck],
  ["tutorials", "الفيديوهات التعليمية", PlayCircle],
  ["tools", "الأدوات", Wrench],
  ["brand", "الهوية والإعدادات", Palette],
];

const CANVA_ERR = (r) => (r === "access_denied" ? "تم رفض الموافقة في كانفا" : r === "bad_state" || r === "expired" ? "انتهت مهلة الربط، اضغط «ربط كانفا» مرة ثانية" : r.includes("invalid_scope") || r.includes("scope") ? "فعّل الصلاحيتين design:meta:read و design:content:read في كانفا" : r.startsWith("token_401") || r.includes("invalid_client") ? "Client Secret غير صحيح — ولّد واحداً جديداً من كانفا" : `حاول مرة أخرى (${r || "غير معروف"})`);

export default function PlatformConsole() {
  const { platform } = useAuth();
  const [me, setMe] = useState(undefined);
  const [sec, setSec] = useState(() => {
    const sp = new URLSearchParams(window.location.search), c = sp.get("canva");
    if (c) { sessionStorage.setItem("pc_sec", "marketing"); sessionStorage.setItem("pc_canva", c === "error" ? `error:${sp.get("reason") || ""}` : c); window.history.replaceState(null, "", "/platform"); }
    return sessionStorage.getItem("pc_sec") || "overview";
  });
  useEffect(() => {
    const c = sessionStorage.getItem("pc_canva");
    if (!c) return;
    sessionStorage.removeItem("pc_canva");
    if (c === "connected") toast.success("تم ربط كانفا ✓ افتح «الإعلانات» ← إعلان جديد ← «استيراد من كانفا»", { duration: 8000 });
    else toast.error(`لم يكتمل ربط كانفا — ${CANVA_ERR(c.slice(6))}`, { duration: 12000 });
  }, []);
  const check = () => {
    if (!localStorage.getItem(PF_TOKEN_KEY)) { setMe(null); return; }
    api.pfMe().then(setMe).catch(() => { localStorage.removeItem(PF_TOKEN_KEY); setMe(null); });
  };
  useEffect(check, []);
  const logout = async () => { try { await api.pfLogout(); } catch { /* ignore */ } localStorage.removeItem(PF_TOKEN_KEY); setMe(null); };
  if (me === undefined) return <div className="grid min-h-screen place-items-center bg-[#0E1A2B] text-slate-400">جارٍ التحميل…</div>;
  if (!me) return <PfLogin onIn={check} />;
  const go = (k) => { setSec(k); sessionStorage.setItem("pc_sec", k); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const title = NAV.find((n) => n[0] === sec)?.[1];
  return (
    <div dir="rtl" className="min-h-screen bg-[#F4F2EE] font-body text-slate-900 lg:flex" data-testid="platform-console">
      <style>{`.pc-rise{animation:pcRise .5s cubic-bezier(.2,.7,.2,1) both}@keyframes pcRise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}`}</style>
      <aside className="sticky top-0 z-30 bg-[#0E1A2B] text-slate-300 lg:h-screen lg:w-64 lg:shrink-0">
        <div className="flex items-center gap-2.5 px-5 py-4 lg:py-6">
          <LogoMark className="h-9 w-9" />
          <div><p className="font-heading text-base font-extrabold text-white">{platform?.name || "WorkZone"}</p><p className="text-[10px] text-emerald-300/80" data-testid="pc-me-name">{me.name}</p></div>
          {me.linked && <a href="/" className="ms-auto rounded-lg p-2 text-emerald-300 hover:bg-white/5 lg:hidden" title="الرجوع لحساب الشركة" data-testid="pc-back-company-mobile"><Building2 className="h-4 w-4" /></a>}
          <button onClick={logout} className={`${me.linked ? "" : "ms-auto "}rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white lg:hidden`} title="تسجيل الخروج" data-testid="pc-logout-mobile"><LogOut className="h-4 w-4" /></button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:overflow-visible lg:pb-0" data-testid="pc-nav">
          {NAV.map(([k, label, Icon]) => (
            <button key={k} onClick={() => go(k)} data-testid={`pc-nav-${k}`}
              className={`flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-colors ${sec === k ? "bg-white/10 font-bold text-white" : "hover:bg-white/5 hover:text-white"}`}>
              <Icon className={`h-4 w-4 ${sec === k ? "text-emerald-300" : ""}`} />{label}
            </button>))}
        </nav>
        <div className="hidden space-y-1 px-3 pt-8 lg:block">
          {me.linked && <a href="/" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-emerald-300 hover:bg-white/5 hover:text-white" data-testid="pc-back-company"><Building2 className="h-4 w-4" /> الرجوع لحساب الشركة</a>}
          <button onClick={logout} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm hover:bg-white/5 hover:text-white" data-testid="pc-logout"><LogOut className="h-4 w-4" /> تسجيل الخروج</button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-8 lg:px-12 lg:py-10">
        <div className="mb-8 flex items-end justify-between gap-3">
          <div><p className="text-xs text-slate-500">منصة WorkZone</p><h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="pc-title">{title}</h1></div>
        </div>
        <div key={sec}>
          {sec === "overview" && <PcOverview go={go} />}
          {sec === "accounting" && <PcAccounting />}
          {sec === "companies" && <div className="rounded-2xl bg-white p-4 sm:p-6"><AdminOrgsDialog embed visibleTabs={["companies", "support"]} initialTab="companies" /></div>}
          {sec === "marketing" && <PcMarketing />}
          {sec === "insurance" && <PcInsurance />}
          {sec === "tutorials" && <PcTutorials />}
          {sec === "tools" && <div className="space-y-4"><PcBackups /><PcRabet /><PcVideoUsage /><PcTools /></div>}
          {sec === "brand" && <div className="space-y-4"><PfLinkCard /><PfPasswordCard /><PfRecoveryEmailCard /><div className="rounded-2xl bg-white p-4 sm:p-6"><AdminOrgsDialog embed visibleTabs={["identity", "announcements", "design"]} initialTab="identity" /></div></div>}
        </div>
      </main>
    </div>
  );
}
